/**
 * HR Module - Dashboard Tab Component
 */
import { Users, Calendar, Award, TrendingDown, AlertOctagon, CalendarDays, DoorOpen, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { StatCard } from './UIComponents';
import { RATING_COLORS, COLORS } from './constants';

const DashboardTab = ({ dash }) => {
  const s = dash?.summary || {};
  const alerts = dash?.alerts || {};

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Staf Aktif" value={s.total_staff || 0} color="border-l-blue-500" />
        <StatCard icon={Award} label="Dinilai" value={s.total_evaluated || 0} color="border-l-emerald-500" />
        <StatCard icon={TrendingDown} label="Prestasi Rendah" value={s.staff_low_performance || 0} color="border-l-red-500" alert={s.staff_low_performance > 0} />
        <StatCard icon={AlertOctagon} label="Amaran Aktif" value={s.active_warnings || 0} color="border-l-orange-500" alert={s.active_warnings > 0} />
        <StatCard icon={CalendarDays} label="Cuti Pending" value={s.pending_leaves || 0} color="border-l-purple-500" />
        <StatCard icon={DoorOpen} label="Turnover YTD" value={s.turnover_ytd || 0} color="border-l-gray-500" />
      </div>

      {/* Attendance Today */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-700" /> Kehadiran Hari Ini
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {['Hadir', 'Cuti', 'MC', 'Lewat', 'Tidak Hadir', 'WFH', 'Outstation'].map((status, i) => (
            <div key={status} className={`p-2 rounded-lg text-center min-w-0 ${[
              'bg-emerald-100 text-emerald-700',
              'bg-blue-100 text-blue-700',
              'bg-amber-100 text-amber-700',
              'bg-orange-100 text-orange-700',
              'bg-red-100 text-red-700',
              'bg-purple-100 text-purple-700',
              'bg-gray-100 text-gray-700'
            ][i]}`}>
              <p className="text-lg sm:text-xl font-bold">{dash?.attendance_today?.[status.toLowerCase().replace(' ', '_')] || 0}</p>
              <p className="text-[11px] sm:text-xs truncate">{status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold mb-3">Taburan Penilaian</h3>
          {dash?.rating_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={dash.rating_distribution} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="count" nameKey="rating">
                  {dash.rating_distribution.map((e, i) => (
                    <Cell key={i} fill={RATING_COLORS[e.rating] || COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-center py-10">Tiada data</p>}
        </div>
        
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold mb-3">Prestasi Jabatan</h3>
          {dash?.department_performance?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dash.department_performance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="department" type="category" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="average_score" fill="#C41E3A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-center py-10">Tiada data</p>}
        </div>
      </div>

      {/* Alerts Section */}
      {(alerts.low_performance?.length > 0 || alerts.active_warnings?.length > 0 || alerts.probation_ending?.length > 0) && (
        <div className="bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> Perlu Perhatian
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-3 border-l-4 border-l-red-500">
              <p className="text-sm font-medium mb-2">Prestasi Rendah ({alerts.low_performance?.length || 0})</p>
              {(alerts.low_performance || []).slice(0, 3).map((i, idx) => (
                <p key={idx} className="text-xs text-gray-600">{i.staff_name} - {i.rating}</p>
              ))}
            </div>
            <div className="bg-white rounded-lg p-3 border-l-4 border-l-orange-500">
              <p className="text-sm font-medium mb-2">Amaran Aktif ({alerts.active_warnings?.length || 0})</p>
              {(alerts.active_warnings || []).slice(0, 3).map((i, idx) => (
                <p key={idx} className="text-xs text-gray-600">{i.staff_name} - {i.type}</p>
              ))}
            </div>
            <div className="bg-white rounded-lg p-3 border-l-4 border-l-blue-500">
              <p className="text-sm font-medium mb-2">Probation Tamat ({alerts.probation_ending?.length || 0})</p>
              {(alerts.probation_ending || []).slice(0, 3).map((i, idx) => (
                <p key={idx} className="text-xs text-gray-600">{i.staff_name} - {i.probation_end}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
